import { ActionContext, ActionTree } from 'vuex'
import {
  IChallengePayload,
  IProductExtended,
  IRootState,
  ISubscriptionPayload,
  ISubscriptionsState,
  IUserProfile,
} from '@/@types'
import { fetchPaymentInfo, fetchSubscriptions, postCheckout } from '@/models/subscriptions'
import { getAccessToken, setAccessToken } from '@internetworkexpert/js-common'

import CONSTANTS from '@/constants'
import { eventsConstants } from '@/helpers/eventsList'
import router from '@/router'
import { getAttributionFields } from '@/helpers/attribution'
import { submitHubspotForm, buildHubspotContext, mapAttributionToHSFields } from '@/helpers/hubspot'
import { buildCartPayload } from '@/helpers/cartPayload'

const {
  PLANS,
  PREMIUM_PLANS,
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  LEAP_YEAR_PROMO_PRODUCT_BUNDLES,
  TCS_PROMO_PRODUCT_BUNDLES,
  SUMMER_SAVINGS_BUNDLE,
} = CONSTANTS

type GuestCheckoutProfile = Partial<IUserProfile> & { password?: string }

const hasGuestProfileRequiredFields = (profile: GuestCheckoutProfile | null) => {
  if (!profile) return false
  const requiredFields: Array<'email' | 'first_name' | 'last_name' | 'password'> = [
    'email',
    'first_name',
    'last_name',
    'password',
  ]
  return requiredFields.every(field => {
    const value = profile[field]
    return typeof value === 'string' && value.trim().length > 0
  })
}

type PaymentEntriesLike = {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  streetAddress?: string
  state?: string
  country?: string
  zip?: string
}

interface IErrorNewUser extends Error {
  error: Error
  new_user?: {
    access_token: string
  }
  processor_error?: string
  processor_error_code?: string
  actionTokenId?: string
}

const actions: ActionTree<ISubscriptionsState, IRootState> = {
  submitPaymentInfo: async (
    context: ActionContext<ISubscriptionsState, IRootState>,
    params: {
      challengeToken: string | null
      captchaToken: string | null
    }
  ) => {
    const { challengeToken, captchaToken } = params
    const selectedPlan = context.rootGetters['shoppingCart/selectedPlan']
    const selectedSkillDivePlan = context.rootGetters['shoppingCart/selectedSkillDivePlan']
    const user_data = context.rootState.auth.userProfile ?? ''
    const couponStr = context.rootGetters['coupons/getEligibleCoupon'] ?? ''
    const billing_token = context.rootGetters['recurly/getRecurlyToken'] ?? ''
    const accessToken = getAccessToken()
    const guestProfile =
      user_data && typeof user_data === 'object' ? (user_data as GuestCheckoutProfile) : null
    if (!accessToken && !hasGuestProfileRequiredFields(guestProfile)) {
      router.push({ name: 'account' })
      throw new Error(
        'Your session expired before checkout. Please create your account again to continue.'
      )
    }
    const shoppingCartItems = context.rootState.shoppingCart.shoppingCart
    const isPromotionActive = context.rootGetters['app/isPromotionActive']
    const promotionItemsCodes = [
      ...Object.values(BLACK_FRIDAY_PRODUCT_BUNDLES),
      ...Object.values(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
      ...Object.values(TCS_PROMO_PRODUCT_BUNDLES),
      ...Object.values(SUMMER_SAVINGS_BUNDLE),
    ]
    let promoBundle: IProductExtended
    if (isPromotionActive) {
      promoBundle = shoppingCartItems.find(i => promotionItemsCodes.includes(i.merchant_id!))!
    }
    const utm = context.rootState.events.utmParams ?? {}
    const cartPayload = buildCartPayload({
      shoppingCart: shoppingCartItems,
      selectedPlan,
      selectedSkillDivePlan,
      availablePlans: context.rootState.products.availablePlans,
      isPromotionActive,
    })

    const payload = {
      ...cartPayload,
      coupon: couponStr,
      billing_token,
      origin: process.env.VUE_APP_ORIGIN !== undefined ? process.env.VUE_APP_ORIGIN : '',
      utm,
      captchaToken,
    }

    const attribution = getAttributionFields(context.rootState?.events?.utmParams || {})
    if (Object.keys(attribution).length) {
      ;(payload as ISubscriptionPayload).hidden_fields = {
        ...((payload as ISubscriptionPayload).hidden_fields || {}),
        ...attribution,
      }
    }

    // cartPayload already includes promo bundle handling

    const portalId =
      process.env.NODE_ENV !== 'production'
        ? process.env.VUE_APP_HUBSPOT_DEVELOPMENT_PORTAL_ID
        : process.env.VUE_APP_HUBSPOT_PORTAL_ID
    const purchaseFormId = process.env.VUE_APP_HUBSPOT_PURCHASE_FORM_ID

    const profile = context.rootState.auth.userProfile || {}
    const paymentEntriesState: PaymentEntriesLike =
      context.rootState.subscriptions?.paymentEntries ||
      ((context.rootState as { payment?: { entries?: PaymentEntriesLike } }).payment?.entries ?? {})
    const emailFromUserData =
      user_data && typeof user_data === 'object' && 'email' in user_data
        ? (user_data as { email?: string }).email
        : undefined
    const email = String(
      (emailFromUserData || profile?.email || paymentEntriesState?.email || '') ?? ''
    ).trim()

    const baseFields = [
      {
        name: 'firstname',
        value: String(profile?.first_name || paymentEntriesState?.firstName || ''),
      },
      {
        name: 'lastname',
        value: String(profile?.last_name || paymentEntriesState?.lastName || ''),
      },
      { name: 'email', value: email },
      {
        name: 'phone',
        value: String(profile?.preferred_phone_number || paymentEntriesState?.phone || ''),
      },
      { name: 'address', value: String(paymentEntriesState?.streetAddress || '') },
      { name: 'state', value: String(paymentEntriesState?.state || '') },
      { name: 'country', value: String(paymentEntriesState?.country || '') },
      { name: 'zip', value: String(paymentEntriesState?.zip || '') },
    ]

    const totalStr = context.rootGetters['priceDetails/getTotalStr'] || ''
    const taxesStr = context.rootGetters['priceDetails/getTaxesStr'] || ''
    const subtotalValue = Number(totalStr) - Number(taxesStr || 0)
    const formattedValue = Number.isFinite(subtotalValue) ? subtotalValue.toFixed(2) : ''

    const HS_PURCHASE_STORAGE_KEY = 'hs_purchase_seen'
    const HS_PURCHASE_STORAGE_TTL = 1000 * 60 * 60 * 24 * 30

    const readHubspotPurchaseToken = () => {
      if (typeof window === 'undefined' || !window.localStorage) return null
      try {
        const rawValue = window.localStorage.getItem(HS_PURCHASE_STORAGE_KEY)
        if (!rawValue) return null

        let parsed: { token?: string; ts?: number } = { token: rawValue }
        try {
          parsed = JSON.parse(rawValue) as { token?: string; ts?: number }
        } catch {
          // Legacy/string format fallback
        }

        if (parsed?.ts && Date.now() - parsed.ts > HS_PURCHASE_STORAGE_TTL) {
          window.localStorage.removeItem(HS_PURCHASE_STORAGE_KEY)
          return null
        }
        return parsed?.token ?? null
      } catch {
        return null
      }
    }

    const writeHubspotPurchaseToken = (token: string) => {
      if (typeof window === 'undefined' || !window.localStorage) return
      try {
        const storagePayload = JSON.stringify({ token, ts: Date.now() })
        window.localStorage.setItem(HS_PURCHASE_STORAGE_KEY, storagePayload)
      } catch {
        // ignore HS errors
      }
    }

    const submitPurchaseToHubspot = async (invoiceNumber?: unknown) => {
      if (typeof window === 'undefined') return

      const normalizedInvoice =
        invoiceNumber !== undefined && invoiceNumber !== null ? String(invoiceNumber).trim() : ''

      const emailForSubmission = String(email || profile?.email || '').trim()
      if (!emailForSubmission.length) {
        if (process.env.NODE_ENV !== 'production') {
          // ignore HS errors
        }
        return
      }

      if (!portalId || !purchaseFormId) {
        if (process.env.NODE_ENV !== 'production') {
          // ignore HS errors
        }
        return
      }

      const submissionToken = normalizedInvoice
        ? `${emailForSubmission}::${normalizedInvoice}`
        : null

      const isDuplicatePurchaseSubmission = () => {
        if (submissionToken === null) return false
        return (
          (window as any).__hsPurchaseSubmitted === submissionToken ||
          readHubspotPurchaseToken() === submissionToken
        )
      }

      if (isDuplicatePurchaseSubmission()) {
        if (process.env.NODE_ENV !== 'production') {
          // ignore HS errors
        }
        return
      }

      let hubspotFields = [...baseFields, ...mapAttributionToHSFields(attribution)]
      if (attribution.gclid && !hubspotFields.some(field => field?.name === 'gclid')) {
        hubspotFields.push({ name: 'gclid', value: attribution.gclid })
      }
      if (attribution.fbclid && !hubspotFields.some(field => field?.name === 'fbclid')) {
        hubspotFields.push({ name: 'fbclid', value: attribution.fbclid })
      }

      if (formattedValue) {
        hubspotFields.push({ name: 'value', value: String(Number(formattedValue)) })
        hubspotFields.push({ name: 'currency', value: 'USD' })
      }

      if (normalizedInvoice.length) {
        hubspotFields.push({ name: 'invoice_number', value: normalizedInvoice })
      }
      hubspotFields = hubspotFields.filter(
        field => field && String(field.value || '').trim().length
      )

      if (!hubspotFields.length) {
        if (process.env.NODE_ENV !== 'production') {
          // ignore HS errors
        }
        return
      }

      const hubspotContext = buildHubspotContext()
      const hubspotSubmission = submitHubspotForm({
        portalId,
        formId: purchaseFormId,
        fields: hubspotFields,
        context: hubspotContext,
      })

      const persistSubmissionToken = () => {
        if (submissionToken === null) return
        ;(window as any).__hsPurchaseSubmitted = submissionToken
        writeHubspotPurchaseToken(submissionToken)
      }

      hubspotSubmission
        .then(() => {
          persistSubmissionToken()
        })
        .catch(error => {
          if (process.env.NODE_ENV !== 'production') {
            // ignore HS errors
          }
        })

      await Promise.race([
        hubspotSubmission,
        new Promise(resolve => setTimeout(resolve, 1500)),
      ]).catch(() => {})
    }

    if (process.env.NODE_ENV !== 'production') {
      // ignore HS errors
    }

    try {
      if (accessToken) {
        const payloadWAccessToken: IChallengePayload = Object.assign({}, payload, {
          payment_challenge_token: challengeToken ?? '',
          accessToken,
        })
        const checkoutResponse = await postCheckout(payloadWAccessToken)
        await submitPurchaseToHubspot(checkoutResponse?.invoice_number)
        return checkoutResponse
      }
      const payloadWUserData: ISubscriptionPayload = Object.assign({}, payload, {
        user_data,
      })
      const returnedResponse = await postCheckout(payloadWUserData)
      await submitPurchaseToHubspot(returnedResponse?.invoice_number)
      const { access_token, user_id } = returnedResponse
      setAccessToken(access_token)
      return returnedResponse
    } catch (errorObject) {
      const error = errorObject as IErrorNewUser
      let attemptResponse
      const { processor_error_code } = error
      const isNewUser = error.new_user
      if (isNewUser) {
        const new_user_access_token = isNewUser.access_token
        setAccessToken(new_user_access_token)
        attemptResponse = await context.dispatch('auth/attemptLogin', new_user_access_token, {
          root: true,
        })
        await context.dispatch(
          'events/sendMixpanelEvent',
          { event_name: eventsConstants.CHECKOUT_USER_SIGNUP },
          {
            root: true,
          }
        )
        attemptResponse.user_id = attemptResponse?.id
      }
      const { message } = error.error ? error.error : (error as Error)
      if (message === 'The email has already been taken.') {
        router.push({
          name: 'account',
          params: { action: 'login' },
        })
        return context.dispatch(
          'app/setError',
          'Payment declined. The email has already been taken.',
          {
            root: true,
          }
        )
      }
      context.commit('THREE_DS_FLOW_UPDATED', false)
      if (processor_error_code && processor_error_code.length) {
        if (processor_error_code === 'three_d_secure_action_required') {
          const { actionTokenId } = error
          const td_access_token = getAccessToken()
          return context.dispatch('trigger3dsFlow', {
            actionTokenId,
            td_access_token,
          })
        }
        if (processor_error_code === 'declined') {
          context.dispatch(
            'app/setError',
            `Your charge has been declined. Please contact your bank if you believe this was a mistake.`,
            {
              root: true,
            }
          )
          if (isNewUser) {
            attemptResponse.error = true
            return attemptResponse
          }
          return false
        }
        context.dispatch(
          'app/setError',
          'There was an issue with the credit card provided. Please contact your bank.',
          {
            root: true,
          }
        )
        if (isNewUser) {
          attemptResponse.error = true
          return attemptResponse
        }
        return false
      }
      if (
        message === 'Token is either invalid or expired' ||
        message === "Trying to get property 'account_code' of non-object"
      ) {
        context.commit('recurly/RECURLY_TOKEN_UPDATED', '')
        router.push({ name: 'payment' })
        return context.dispatch(
          'app/setError',
          'Session expired. Please re-enter your payment details.',
          {
            root: true,
          }
        )
      }
      if (isPromotionActive && promoBundle!) {
        if (
          message ===
          'The plans field is required when none of items / bundle are present., The items field is required when none of plans / bundle are present.'
        ) {
          return context.dispatch(
            'app/setError',
            'The INE Security Certification field is required. Please choose a certification.',
            {
              root: true,
            }
          )
        }
      }
      context.dispatch('app/setError', message, { root: true })
    } finally {
      context.dispatch('app/setIsComponentLoading', false, { root: true })
    }
  },
  setPartnerStackFlow: (
    context: ActionContext<ISubscriptionsState, IRootState>,
    payload: boolean
  ) => {
    context.commit('PARTNER_STACK_FLOW_UPDATED', payload)
  },
  trigger3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, { actionTokenId }) => {
    context.dispatch('recurly/setRecurlyTokenActionId', actionTokenId, {
      root: true,
    })
    context.commit('THREE_DS_FLOW_UPDATED', true)
  },
  resolve3dsFlow: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.dispatch('app/setIsComponentLoading', true, { root: true })
    context.commit('recurly/RECURLY_ACTION_TOKEN_RESULT_UPDATED', payload)
    context.commit('THREE_DS_FLOW_UPDATED', false)
  },
  getCurrentPaymentMethodOnFile: async (
    context: ActionContext<ISubscriptionsState, IRootState>
  ) => {
    try {
      const method = await fetchPaymentInfo()
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', method)
      return method
    } catch (error) {
      context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
    }
  },
  getSubscriptionsWithPasses: async (context: ActionContext<ISubscriptionsState, IRootState>) => {
    try {
      const response = await fetchSubscriptions()
      context.commit('USER_SUBSCRIPTIONS_WITH_PASSES_UPDATED', response)
      return response
    } catch (error) {
      context.commit('USER_SUBSCRIPTIONS_WITH_PASSES_UPDATED', '')
    }
  },
  removePaymentMethodOnFile: async (context: ActionContext<ISubscriptionsState, IRootState>) => {
    context.commit('PAYMENT_METHOD_ON_FILE_UPDATED', '')
  },
  updatePaymentEntries: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_ENTRIES_UPDATED', payload)
  },
  setPaymentMethod: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PAYMENT_METHOD_UPDATED', payload)
  },
  setPromotionalField: (context: ActionContext<ISubscriptionsState, IRootState>, payload) => {
    context.commit('PROMOTIONAL_FIELD_UPDATED', payload)
  },
}

export default actions
